import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  supabase,
  isSupabaseConfigured,
  selectData,
  insertData,
} from "../lib/supabase";

const emptyProduct = {
  name: "",
  description: "",
  default_unit_price: "",
  default_tax_rate: 20,
  is_active: true,
};

export default function AdminProducts() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [message, setMessage] = useState("");
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({ ...emptyProduct });

  useEffect(() => {
    loadProducts();
  }, []);

  async function loadProducts() {
    try {
      setLoading(true);
      setMessage("");

      const result = await selectData("products", {
        order: { column: "created_at", ascending: false },
      });

      if (result.error) throw result.error;

      setProducts(result.data || []);
    } catch (error) {
      console.error(error);
      setMessage("Impossible de charger les produits.");
    } finally {
      setLoading(false);
    }
  }

  function updateField(field, value) {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function resetForm() {
    setForm({ ...emptyProduct });
    setEditingId(null);
  }

  function startEdit(product) {
    setEditingId(product.id);
    setForm({
      name: product.name || "",
      description: product.description || "",
      default_unit_price: product.default_unit_price ?? "",
      default_tax_rate: product.default_tax_rate ?? 20,
      is_active: product.is_active ?? true,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setSaving(true);
      setMessage("");

      if (!form.name.trim()) {
        setMessage("Le nom du produit est obligatoire.");
        return;
      }

      const payload = {
        name: form.name.trim(),
        description: form.description?.trim() || null,
        default_unit_price: Number(form.default_unit_price || 0),
        default_tax_rate: Number(form.default_tax_rate || 0),
        is_active: !!form.is_active,
      };

      if (!isSupabaseConfigured || !supabase) {
        setMessage("Mode démo : impossible d’enregistrer le produit.");
        return;
      }

      if (editingId) {
        const { error } = await supabase
          .from("products")
          .update(payload)
          .eq("id", editingId);

        if (error) throw error;

        setMessage("Produit mis à jour avec succès.");
      } else {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        const result = await insertData("products", {
          ...payload,
          created_by: session?.user?.id || null,
        });

        if (result.error) throw result.error;

        setMessage("Produit créé avec succès.");
      }

      resetForm();
      await loadProducts();
    } catch (error) {
      console.error(error);
      setMessage("Erreur lors de l’enregistrement du produit.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleProductStatus(product) {
    try {
      if (!supabase) {
        setMessage("Supabase n'est pas configuré.");
        return;
      }

      const { error } = await supabase
        .from("products")
        .update({ is_active: !product.is_active })
        .eq("id", product.id);

      if (error) throw error;

      setProducts((prev) =>
        prev.map((p) =>
          p.id === product.id ? { ...p, is_active: !p.is_active } : p
        )
      );
    } catch (error) {
      console.error(error);
      setMessage("Impossible de modifier le statut du produit.");
    }
  }

  function formatMoney(value) {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(Number(value || 0));
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.topbar}>
          <div>
            <h1 style={styles.title}>Gérer les produits</h1>
            <p style={styles.subtitle}>
              Ajoute, modifie ou désactive les produits et services proposés sur tes factures.
            </p>
          </div>

          <div style={styles.actions}>
            <Link to="/admin" style={styles.secondaryButton}>
              Dashboard
            </Link>
            <Link to="/admin/factures/nouvelle" style={styles.primaryButton}>
              Nouvelle facture
            </Link>
          </div>
        </div>

        {message ? (
          <div
            style={
              message.toLowerCase().includes("succès") ||
              message.toLowerCase().includes("mis à jour") ||
              message.toLowerCase().includes("créé")
                ? styles.alertSuccess
                : styles.alertError
            }
          >
            {message}
          </div>
        ) : null}

        <div style={styles.grid}>
          <section style={styles.card}>
            <h2 style={styles.cardTitle}>
              {editingId ? "Modifier le produit" : "Créer un produit"}
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={styles.field}>
                <label>Nom du produit</label>
                <input
                  style={styles.input}
                  value={form.name}
                  onChange={(e) => updateField("name", e.target.value)}
                  placeholder="Ex. Site vitrine"
                />
              </div>

              <div style={styles.field}>
                <label>Description</label>
                <textarea
                  style={styles.textarea}
                  rows={4}
                  value={form.description}
                  onChange={(e) => updateField("description", e.target.value)}
                  placeholder="Description du service ou produit"
                />
              </div>

              <div style={styles.twoCols}>
                <div style={styles.field}>
                  <label>Prix unitaire HT</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    style={styles.input}
                    value={form.default_unit_price}
                    onChange={(e) =>
                      updateField("default_unit_price", e.target.value)
                    }
                    placeholder="0.00"
                  />
                </div>

                <div style={styles.field}>
                  <label>TVA (%)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    style={styles.input}
                    value={form.default_tax_rate}
                    onChange={(e) =>
                      updateField("default_tax_rate", e.target.value)
                    }
                  />
                </div>
              </div>

              <div style={styles.checkboxRow}>
                <input
                  id="is_active"
                  type="checkbox"
                  checked={!!form.is_active}
                  onChange={(e) => updateField("is_active", e.target.checked)}
                />
                <label htmlFor="is_active">Produit actif</label>
              </div>

              <div style={styles.formActions}>
                <button type="submit" style={styles.primaryButton} disabled={saving}>
                  {saving
                    ? "Enregistrement..."
                    : editingId
                    ? "Mettre à jour"
                    : "Créer le produit"}
                </button>

                {editingId ? (
                  <button
                    type="button"
                    style={styles.secondaryButton}
                    onClick={resetForm}
                  >
                    Annuler
                  </button>
                ) : null}
              </div>
            </form>
          </section>

          <section style={styles.card}>
            <h2 style={styles.cardTitle}>Liste des produits</h2>

            {loading ? (
              <p>Chargement...</p>
            ) : products.length === 0 ? (
              <p>Aucun produit enregistré.</p>
            ) : (
              <div style={styles.productsList}>
                {products.map((product) => (
                  <div key={product.id} style={styles.productItem}>
                    <div style={styles.productHeader}>
                      <div>
                        <div style={styles.productName}>{product.name}</div>
                        <div style={styles.productMeta}>
                          {formatMoney(product.default_unit_price)} • TVA{" "}
                          {Number(product.default_tax_rate || 0)}%
                        </div>
                      </div>

                      <span
                        style={{
                          ...styles.statusBadge,
                          background: product.is_active
                            ? "rgba(80, 200, 120, 0.16)"
                            : "rgba(255, 87, 87, 0.14)",
                          borderColor: product.is_active
                            ? "rgba(80, 200, 120, 0.35)"
                            : "rgba(255, 87, 87, 0.35)",
                          color: product.is_active ? "#8af0ad" : "#ff9a9a",
                        }}
                      >
                        {product.is_active ? "Actif" : "Inactif"}
                      </span>
                    </div>

                    {product.description ? (
                      <p style={styles.productDescription}>{product.description}</p>
                    ) : null}

                    <div style={styles.productActions}>
                      <button
                        type="button"
                        style={styles.smallPrimaryButton}
                        onClick={() => startEdit(product)}
                      >
                        Modifier
                      </button>

                      <button
                        type="button"
                        style={styles.smallSecondaryButton}
                        onClick={() => toggleProductStatus(product)}
                      >
                        {product.is_active ? "Désactiver" : "Réactiver"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    background: "#0b1020",
    minHeight: "100vh",
    padding: "40px 20px",
    color: "#fff",
  },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
  },
  topbar: {
    display: "flex",
    justifyContent: "space-between",
    gap: "20px",
    flexWrap: "wrap",
    alignItems: "flex-start",
    marginBottom: "24px",
  },
  title: {
    fontSize: "2rem",
    fontWeight: 800,
    marginBottom: "8px",
  },
  subtitle: {
    color: "rgba(255,255,255,0.72)",
    lineHeight: 1.6,
    maxWidth: "760px",
  },
  actions: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1.2fr",
    gap: "24px",
  },
  card: {
    background: "#121933",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "18px",
    padding: "24px",
  },
  cardTitle: {
    fontSize: "1.25rem",
    fontWeight: 800,
    marginBottom: "18px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "14px",
  },
  input: {
    marginTop: "6px",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.14)",
    background: "#0d1430",
    color: "#fff",
    outline: "none",
  },
  textarea: {
    marginTop: "6px",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.14)",
    background: "#0d1430",
    color: "#fff",
    outline: "none",
    resize: "vertical",
  },
  twoCols: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginTop: "8px",
    marginBottom: "18px",
  },
  formActions: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  primaryButton: {
    padding: "12px 16px",
    borderRadius: "10px",
    background: "#4f7cff",
    color: "#fff",
    textDecoration: "none",
    fontWeight: 700,
    border: "none",
    cursor: "pointer",
  },
  secondaryButton: {
    padding: "12px 16px",
    borderRadius: "10px",
    background: "#1a2347",
    color: "#fff",
    textDecoration: "none",
    fontWeight: 600,
    border: "1px solid rgba(255,255,255,0.12)",
    cursor: "pointer",
  },
  productsList: {
    display: "grid",
    gap: "14px",
  },
  productItem: {
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "16px",
    padding: "16px",
    background: "#0f1734",
  },
  productHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "flex-start",
    flexWrap: "wrap",
    marginBottom: "8px",
  },
  productName: {
    fontSize: "1rem",
    fontWeight: 800,
    marginBottom: "4px",
  },
  productMeta: {
    color: "rgba(255,255,255,0.68)",
    fontSize: "0.92rem",
  },
  productDescription: {
    color: "rgba(255,255,255,0.78)",
    lineHeight: 1.6,
    marginTop: "8px",
    marginBottom: "14px",
  },
  productActions: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  smallPrimaryButton: {
    padding: "8px 12px",
    borderRadius: "8px",
    background: "#4454ff",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    fontWeight: 700,
  },
  smallSecondaryButton: {
    padding: "8px 12px",
    borderRadius: "8px",
    background: "#1a2347",
    color: "#fff",
    border: "1px solid rgba(255,255,255,0.12)",
    cursor: "pointer",
    fontWeight: 600,
  },
  statusBadge: {
    padding: "6px 10px",
    borderRadius: "999px",
    border: "1px solid transparent",
    fontWeight: 700,
    fontSize: "0.82rem",
  },
  alertError: {
    padding: "12px 14px",
    borderRadius: "12px",
    background: "rgba(255, 87, 87, 0.12)",
    border: "1px solid rgba(255, 87, 87, 0.28)",
    marginBottom: "18px",
  },
  alertSuccess: {
    padding: "12px 14px",
    borderRadius: "12px",
    background: "rgba(80, 200, 120, 0.12)",
    border: "1px solid rgba(80, 200, 120, 0.28)",
    marginBottom: "18px",
  },
};